/**
 * BestOddsTimeWindowFilter - Preset chips for kickoff time window.
 *
 * Presets: all | today | next24h | next48h.
 * Kickoff is interpreted in the client's local timezone (see bestOddsFilters).
 */

import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useBestOdds } from '../context/BestOddsContext';
import { useTranslation } from '../../i18n/hooks';
import { TIME_WINDOW_VALUES } from '../utils/bestOddsFilters';
import './BestOddsFilterBar.css';

const TIME_WINDOW_LABEL_KEYS = {
  all: 'bestOdds.filters.timeWindowAll',
  today: 'bestOdds.filters.timeWindowToday',
  next24h: 'bestOdds.filters.timeWindowNext24h',
  next48h: 'bestOdds.filters.timeWindowNext48h'
};

const BestOddsTimeWindowFilter = ({ className = '' }) => {
  const { t } = useTranslation();
  const { filters, setTimeWindow } = useBestOdds();

  const activeWindow = TIME_WINDOW_VALUES.includes(filters?.timeWindow)
    ? filters.timeWindow
    : 'all';

  const handleSelect = useCallback((value) => {
    setTimeWindow(value);
  }, [setTimeWindow]);

  return (
    <div
      className={`best-odds-filter-bar__chip-row ${className}`.trim()}
      role="group"
      aria-label={t('bestOdds.filters.timeWindowAriaLabel')}
    >
      <span className="best-odds-filter-bar__chip-row-label" id="best-odds-time-window-label">
        {t('bestOdds.filters.timeWindowLabel')}
      </span>
      <div
        className="best-odds-filter-bar__chip-row-chips"
        role="group"
        aria-labelledby="best-odds-time-window-label"
      >
        {TIME_WINDOW_VALUES.map((value) => {
          const isActive = activeWindow === value;
          const label = t(TIME_WINDOW_LABEL_KEYS[value]);
          const ariaLabel = value === 'all'
            ? t('bestOdds.filters.timeWindowAllAriaLabel')
            : label;

          return (
            <button
              key={value}
              type="button"
              className={`best-odds-filter-bar__chip${isActive ? ' is-active' : ''}`}
              aria-pressed={isActive}
              aria-label={ariaLabel}
              onClick={() => handleSelect(value)}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

BestOddsTimeWindowFilter.propTypes = {
  className: PropTypes.string
};

export default BestOddsTimeWindowFilter;
